import type { AuthenticatedUser, UserRole } from '@/api/types';

/**
 * Who this app is for, decided once at the door.
 *
 * Every screen here works inside one branch, so an account with no branch has
 * nothing to do once signed in. Letting it in only to show "no pharmacy linked"
 * makes a correct password look like a broken app. The check runs at sign-in
 * (refused on the login page, before any token is kept) and again on every
 * restored session (a branch can be unlinked while someone is signed in).
 *
 * Administrators are the exception: they belong to no branch and may open one
 * by id. The server checks every request regardless; this only decides who is
 * shown the door instead of an empty app.
 */
export type CounterAccess = 'ok' | 'no-branch' | 'not-staff';

const ADMIN_ROLES: readonly UserRole[] = ['ADMIN', 'SUPER_ADMIN', 'SUPPORT'];
const STAFF_ROLES: readonly UserRole[] = ['PHARMACIST', 'PHARMACY_STAFF'];

export function isAdmin(user: Pick<AuthenticatedUser, 'roles'> | null | undefined): boolean {
  return Boolean(user?.roles.some((role) => ADMIN_ROLES.includes(role)));
}

export function counterAccess(user: Pick<AuthenticatedUser, 'roles' | 'pharmacyIds'>): CounterAccess {
  if ((user.pharmacyIds ?? []).length > 0) return 'ok';
  if (isAdmin(user)) return 'ok';
  if (user.roles.some((role) => STAFF_ROLES.includes(role))) return 'no-branch';
  return 'not-staff';
}

/** What the login page says, in what happened, why, and what to do. */
export const ACCESS_MESSAGE: Record<Exclude<CounterAccess, 'ok'>, { title: string; detail: string }> =
  {
    'no-branch': {
      title: 'This account is not linked to a pharmacy yet',
      detail:
        'Your password is right, but no branch has added you as staff. Ask your administrator to add you to your branch, then sign in again.',
    },
    'not-staff': {
      title: 'This app is for pharmacy staff',
      detail:
        'Customers order on the customer app, and riders use the delivery app. If you work at a pharmacy, ask your administrator to add you as staff.',
    },
  };

/** Thrown by sign-in when the password is right but the door is not. */
export class AccessRefusedError extends Error {
  readonly access: Exclude<CounterAccess, 'ok'>;

  constructor(access: Exclude<CounterAccess, 'ok'>) {
    super(ACCESS_MESSAGE[access].title);
    this.name = 'AccessRefusedError';
    this.access = access;
  }
}
